import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import socketService from '../services/socket';
import { useAuth } from './AuthContext';

const SocketContext = createContext(null);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const { user } = useAuth();
  const token = localStorage.getItem('access_token');
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [reconnecting, setReconnecting] = useState(false);

  // Handle connection status changes
  const handleConnectionStatus = useCallback((data) => {
    setIsConnected(data.connected);
    if (data.connected) {
      setConnectionError(null);
      setReconnecting(false);
    } else {
      if (data.reason === 'transport error' || data.reason === 'ping timeout') {
        setReconnecting(true);
      }
    }
  }, []);

  // Handle connection errors
  const handleConnectionError = useCallback((data) => {
    setConnectionError(data.error);
    setReconnecting(false);
  }, []);

  // Initialize socket connection when user logs in
  useEffect(() => {
    if (user && token) {
      console.log('Initializing socket connection for user:', user.username);

      // Connect to socket
      socketService.connect(token);

      // Subscribe to connection status changes
      socketService.on('connection_status', handleConnectionStatus);
      socketService.on('connection_error', handleConnectionError);

      // Join user-specific room
      if (socketService.isConnected()) {
        socketService.joinRoom(`user_${user.id}`);
        socketService.joinRoom(`role_${user.role}`);
      }

      return () => {
        // Cleanup listeners but don't disconnect (let logout handle that)
        socketService.off('connection_status', handleConnectionStatus);
        socketService.off('connection_error', handleConnectionError);
      };
    }
  }, [user, token, handleConnectionStatus, handleConnectionError]);

  // Disconnect socket when user logs out
  useEffect(() => {
    if (!user) {
      console.log('User logged out, disconnecting socket');
      socketService.disconnect();
      setIsConnected(false);
      setConnectionError(null);
      setReconnecting(false);
    }
  }, [user]);

  // Provide socket context
  const value = {
    socket: socketService,
    isConnected,
    connectionError,
    reconnecting,
    // Helper methods
    on: (event, callback) => socketService.on(event, callback),
    off: (event, callback) => socketService.off(event, callback),
    emit: (event, data) => socketService.emit(event, data),
    joinRoom: (room) => socketService.joinRoom(room),
    leaveRoom: (room) => socketService.leaveRoom(room),
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

export default SocketContext;
